import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { Icon } from '@/components/brand/Brand'
import { weekDemo } from '@/components/motion/demos/weekDemo'
import { useMotion } from '@/components/motion/motion'
import { useDemo } from '@/components/motion/useDemo'

type FirmState = 'today' | 'united'

/* "Your firm": what stays yours, and the team's week Today and With United. The week replays
   Today to With United each time the card comes into view, until the visitor picks a state. */
export const FirmCard = () => {
  const { paused, reduceMotion } = useMotion()
  const cardRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const week = useDemo(stageRef, weekDemo)
  const [state, setState] = useState<FirmState>('united')
  const autoplay = useRef(true)
  const timer = useRef(0)

  const showToday = () => {
    window.clearTimeout(timer.current)
    week.current?.pre()
    setState('today')
  }

  const showUnited = (animate: boolean) => {
    window.clearTimeout(timer.current)
    setState('united')
    if (animate && !paused && !reduceMotion) week.current?.run()
    else week.current?.fin()
  }

  const onVisibilityChange = useEffectEvent((visible: boolean) => {
    if (!autoplay.current) return
    if (!visible) {
      showToday()
      return
    }
    if (paused) {
      showUnited(false)
      return
    }
    showToday()
    timer.current = window.setTimeout(() => {
      if (autoplay.current) showUnited(true)
    }, 1000)
  })

  const onPause = useEffectEvent(() => showUnited(false))

  useEffect(() => {
    week.current?.fin()
  }, [week])

  useEffect(() => {
    const card = cardRef.current
    if (!card || reduceMotion || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(([entry]) => onVisibilityChange(entry.isIntersecting), { threshold: 0.55 })
    observer.observe(card)
    return () => {
      observer.disconnect()
      window.clearTimeout(timer.current)
    }
  }, [reduceMotion])

  // Pausing shows the finished week.
  useEffect(() => {
    if (!paused) return
    const pending = window.setTimeout(() => onPause())
    return () => window.clearTimeout(pending)
  }, [paused])

  const choose = (next: FirmState) => {
    autoplay.current = false
    if (next === 'today') showToday()
    else showUnited(true)
  }

  return (
    <div ref={cardRef} className="x-firm-card" data-state={state}>
      <div className="x-fc-head">
        <p className="x-fc-title">Your firm</p>
        <div className="x-fc-toggle" role="group" aria-label="Show your firm">
          <button type="button" aria-pressed={state === 'today'} onClick={() => choose('today')}>
            Today
          </button>
          <button type="button" aria-pressed={state === 'united'} onClick={() => choose('united')}>
            With United
          </button>
        </div>
      </div>
      <div className="x-fc-row">
        <p className="x-fc-label">Stays yours</p>
        <ul className="x-fc-keeps">
          <li>
            <Icon name="check" />
            You run it
          </li>
          <li>
            <Icon name="check" />
            Your name
          </li>
          <li>
            <Icon name="check" />
            Your clients
          </li>
        </ul>
      </div>
      <div ref={stageRef} className="m-stage m-week" aria-hidden="true" />
    </div>
  )
}
